import { useEffect, useState } from "react";
import { Box, Container, Button } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { DJSimpleCodeBlock, ICodeBlock } from "../../main/components/codeblock";
import { IProgress, ISessionProps } from "../../main/components/IProgress";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@appsflyer/fe-ui-core";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import transition from "../../main/components/transition";
import { amplitudeTrack_continue } from "../../services/utils";

const DL_implementFlutter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState<IProgress[]>([]);
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    if (location.state) {
      const state: ISessionProps = location.state as ISessionProps;
      setProgress(state?.progress);
    }
  }, [location.state]);

  const handleClick = () => {
    const progressArr: IProgress[] = progress.slice();
    progressArr.push({
      id: "dl_dltype",
      text: "progress_DL_type",
      order: 4,
    });
    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
    };
    amplitudeTrack_continue(location.state);

    navigate(`/deeplink/${location.state?.os}/deeplinktype`, { state: progressState });
  };

  const code1 = `import 'package:appsflyer_sdk/appsflyer_sdk.dart';`
  const code2 = `AppsFlyerOptions appsFlyerOptions = AppsFlyerOptions(
  afDevKey: "sQ84wpdxRTR4RMCaE9YqS4",
  appId: "1292821412",
  showDebug: true,
);

AppsflyerSdk appsflyerSdk = AppsflyerSdk(appsFlyerOptions);
appsflyerSdk.initSdk(
  registerConversionDataCallback: true,
  registerOnAppOpenAttributionCallback: true,
  registerOnDeepLinkingCallback: true
);`

  const code3 = `_appsflyerSdk?.onDeepLinking((res) {
  // will be implemented in the following steps.
}`;

  const code4 = ` _appsflyerSdk?.onDeepLinking((res) {
  switch (res.status) {
    case Status.FOUND:
      debugPrint("Deep Link was found.");
      break;
    case Status.NOT_FOUND:
      debugPrint("Deep Link was notfound");
      break;
    case Status.ERROR:
      debugPrint("Deep Link error: \${res.error}");
      break;
  }
});`;
  const code5 = `_appsflyerSdk?.onDeepLinking((res) {
  switch (res.status) {
    case Status.FOUND:
      debugPrint("Deep Link was found.");
      debugPrint("The deep link data is: \${res.deepLink}");
      break;
    case Status.NOT_FOUND:
      debugPrint("Deep Link was notfound");
      break;
    case Status.ERROR:
      debugPrint("Deep Link error: \${res.error}");
      break;
  }
});`;

  const code6 = `_appsflyerSdk?.onDeepLinking((res) {
  switch (res.status) {
    case Status.FOUND:
      debugPrint("Deep Link was found.");
      debugPrint("The deep link data is: \${res.deepLink}");
      final deepLinkDestination = res.deepLink.deeplinkValue;
      break;
    case Status.NOT_FOUND:
      debugPrint("Deep Link was notfound");
      break;
    case Status.ERROR:
      debugPrint("Deep Link error: \${res.error}");
      break;
  }
});`;
  const code7 = `_appsflyerSdk?.onDeepLinking((res) {
  switch (res.status) {
    case Status.FOUND:
      debugPrint("Deep Link was found.");
      debugPrint("The deep link data is: \${res.deepLink}");
      final deepLinkDestination = res.deepLink.deeplinkValue;
      final isDeferred = res.deepLink?.isDeferred == true;
      if (isDeferred) {
        // Your code for deferred deep link
      } else {
        // Your code for direct deep link
      }
      break;
    case Status.NOT_FOUND:
      debugPrint("Deep Link was notfound");
      break;
    case Status.ERROR:
      debugPrint("Deep Link error: \${res.error}");
      break;
  }
});`;


  const codeBlockArray: ICodeBlock[] = [
    {
      codePrimary: code1,
      showLineNumbers: true,
      language: "dart"
    },
    {
      codePrimary: code2,
      showLineNumbers: true,
      language: "dart",
      highlight: "11"
    },
    {
      codePrimary: code3,
      showLineNumbers: true,
      language: "dart"
    },
    {
      codePrimary: code4,
      showLineNumbers: true,
      language: "dart"
    },
    {
      codePrimary: code5,
      showLineNumbers: true,
      language: "dart",
      highlight: "5"
    },
    {
      codePrimary: code6,
      showLineNumbers: true,
      language: "dart",
      highlight: "6"
    },
    {
      codePrimary: code7,
      showLineNumbers: true,
      language: "dart",
      highlight: "7-9,10-12"
    }
  ];


  const steps = [
    {
      label: t("dl_implement_flutter_step1_label"),
      component: (
        <>
          <Typography>
            {t("dl_implement_flutter_step1_description")}
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[0]} />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    }, 
    {
      label: t("dl_implement_flutter_step2_label"),
      component: (
        <>
          <DJSimpleCodeBlock {...codeBlockArray[1]} />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("dl_implement_flutter_step3_label"),
      component: (
        <>
          <Typography>
            <code>onDeepLinking()</code> {" "}
            {t("dl_implement_flutter_step3_description_a")} {" "}
            <code>DeepLinkResult</code> {" "}
            {t("dl_implement_flutter_step3_description_b")} {" "}
            <code>onDeepLinking()</code>.
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[2]} />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("dl_implement_flutter_step4_label"),
      component: (
        <>
          <Typography>
            {t("dl_implement_flutter_step4_description_a")} {" "}
            <code>status</code> {" "}
            {t("dl_implement_flutter_step4_description_b")}
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[3]} />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("dl_implement_flutter_step5_label"),
      component: (
        <>
          <Typography>
            <Trans
              i18nKey="dl_implement_flutter_step5_description"
              components={{ code: <code /> }}
            />
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[4]} />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("dl_implement_flutter_step6_label"),
      component: (
        <>
          <Typography>
          {t("dl_implement_flutter_step6_description_a")}{" "}
            <code>deep_link_value</code>{" "}
            {t("dl_implement_flutter_step6_description_b")}{" "}
            <code>deep_link_value</code>{" "}
            {t("dl_implement_flutter_step6_description_c")}{" "}
            <code>deeplinkValue</code>.
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[5]} />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("dl_implement_flutter_step7_label"),
      component: (
        <>
          <Typography>
            <code>isDeferred()</code>{" "}
            {t("dl_implement_flutter_step7_description")}
          </Typography>
          <DJSimpleCodeBlock {...codeBlockArray[6]} />
        </>
      ),
      shouldDisplayNavigationButtons: true,
    }
  ];

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} marginBottom={-10} style={{ minHeight: "50vh" }}>
        <Box marginBottom={2}>
          <Typography variant="h1">
            {t("dl_page9_android_pageTitle")}
          </Typography>
          <Stepper
            activeStep={activeStep}
            style={{ border: "none" }}
            orientation="vertical"
          >
            {steps.map(
              ({ label, component, shouldDisplayNavigationButtons }) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                  <StepContent>
                    {component}
                    {shouldDisplayNavigationButtons && (
                      <Box sx={{ mt: 2 }}>
                        {activeStep !== 0 && (
                          <Button
                            variant="text"
                            color="primary"
                            onClick={handleBack}
                          >
                            {t("general_Back")}
                          </Button>
                        )}
                        {activeStep !== steps.length - 1 && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                          >
                            {activeStep === steps.length - 1
                              ? t("general_Finish")
                              : t("general_Next")}
                          </Button>
                        )}
                      </Box>
                    )}
                  </StepContent>
                </Step>
              )
            )}
          </Stepper>
        </Box>
      </Box>

      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={() => handleClick()}
        >
          {t("general_Continue")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(DL_implementFlutter);
